import { AccessKeyType } from "merchant-portal-components/AccountSettings/AccessKeys/AccessKeys";
import { ButtonTheme } from "merchant-portal-components/AccountSettings/PlacementSettings/types";
import { Sections } from "merchant-portal-components/Carts/Form/Section";
import {
	ChartOption,
	MetricsOption,
} from "merchant-portal-components/DataInsightsView/LoanHistory/constants";
import { NotificationCenterRowType } from "merchant-portal-components/Header/NotificationCenter/NotificationCenterRow";
import {
	MainNavSection,
	SubNavSection,
} from "merchant-portal-components/PrimaryNavigation/NavDrawerSection";
import {
	SearchTypes,
	TransactionActions,
	TransactionStatuses,
} from "merchant-portal-components/TransactionsView/constants";
import { TransactionDetailTab } from "merchant-portal-components/TransactionsView/Modal/TransactionAction/TransactionAction";
import { UserType } from "merchant-portal-frontend/src/global.constants";
import { UserRole } from "merchant-portal-frontend/src/model/api/types";

export interface ScreenResolutionType {
	resolution: string;
}

export enum EventName {
	MerchantIdCopied = "merchant_id_copied",
	DateRangerPickerClick = "date_range_picker_click",
	DateRangeStaticSelection = "date_range_static_selection",
	DateRangeSelection = "date_range_selection",
	StatusFilterSelection = "status_filter_selection",
	PaymentProductFilterSelection = "payment_product_filter_selection",
	PaymentProductAddProductClick = "add_product",
	PaymentProductArchiveClick = "archive_payment_product",
	PaymentProductEditClick = "edit_payment_product",
	PaymentProductCopyClick = "copy_payment_product",
	PaymentProductDeleteScheduledClick = "delete_scheduled_payment_product",
	CartsFilterSelection = "carts_filter_selection",
	SearchTypeSelection = "search_type",
	SearchRan = "search_ran",
	HelpClick = "help_click",
	LegalDocumentClick = "legal_document_click",
	ToggleDisplayMode = "toggle_display_mode",
	TransactionsViewPageChange = "transactions_view_page_change",
	KpiClick = "kpi_click",
	UserManagementClick = "user_management_click",
	AccountSettingsClick = "account_settings_click",
	HelpAndLegalClick = "help_and_legal_click",
	NotificationAlertsClick = "notification_alerts_click",
	UserNameClick = "user_name_click",
	LoginSuccess = "login_success",
	LogoutClick = "logout_click",
	LogoutInactivity = "logout_inactivity",
	AddNewUser = "new_user",
	RemoveUser = "remove_user",
	UpdateUserRole = "update_user_role",
	PrimaryNavigationClick = "primary_navigation_click",
	TransactionIdCopy = "transaction_id_copy",
	ClickTransactionAction = "click_transaction_action",
	ClickTransactionDetailTab = "click_transaction_detail_tab",
	ClickTooltip = "click_tooltip",
	CsvFileDownload = "track_csv_file_download",
	AccountSettingsUpdate = "account_settings_update",
	UnsavedChangesNavigation = "unsaved_changes_navigation",
	DiscardAccountSettingsChanges = "disacrd_account_settings_changes",
	DeleteEmailNotification = "delete_email_notification",
	AddEmailNotification = "add_email_notification",
	UpdateEmailNotification = "update_email_notification",
	AccessKeyCopied = "access_key_copied",
	NotificationLinkClick = "notification_link_click",
	DataInsightCardClick = "track_data_insight_card_click",
	DataInsightDateOptionClick = "track_data_insight_date_option_click",
	CreateCartClick = "create_cart_click",
	ExpireCart = "expire_cart",
	ResendCartEmail = "resend_cart_email",
	CartLinkCopy = "cart_link_copy",
	CreateCartEnterCartTotal = "create_cart_enter_cart_total",
	CreateCartAddItemsToCart = "create_cart_add_items_to_cart",
	CreateCartAddOrderItem = "create_cart_add_order_item",
	CreateCartEditOrderItem = "create_cart_edit_order_item",
	CreateCartRemoveOrderItem = "create_cart_remove_order_item",
	CreateCartSaveCart = "create_cart_save_cart",
	CreateCartDiscardCart = "create_cart_discard_cart",
	EditCartDiscardChanges = "edit_cart_discard_changes",
	EditCartSaveChanges = "edit_cart_save_changes",
	ViewCartGoBackToCartsList = "view_cart_go_back_to_carts_list",
	CreateCartAccordionAction = "create_cart_accordion_action",
	TransactionListExportIconClick = "track_transaction_list_export_icon_click",
	MerchantPlacementThemeSelection = "merchant_placement_theme_selection",
	ViewCartClick = "view_cart_click",
	GeneratingLocationVerificationCodes = "generating_location_verification_codes",
	LocationVerificationCodeCopy = "location_verification_code_copy",
	CheckoutDesign = "checkout_design",
	CheckoutDesignSave = "checkout_design_save",
	CheckoutDesignCancel = "checkout_design_cancel",
	PrimaryColorGradient = "primary_color_gradient",
	HashCodePrimaryColor = "hash_code_primary_color",
	ImageInsert = "image_insert",
}

type RegisterMerchantTenantBaseParams = {
	merchantId: string;
	tenantId: string;
	userId: string;
};

type RegisterMerchantTenantUserTypeParams = RegisterMerchantTenantBaseParams & {
	userType: UserType;
	userEmail?: never;
};

type RegisterMerchantTenantUserEmailParams = RegisterMerchantTenantBaseParams & {
	userEmail: string;
	userType?: never;
};

export interface TrackingType {
	registerCurrentMerchantAndTenant: (
		params: RegisterMerchantTenantUserTypeParams | RegisterMerchantTenantUserEmailParams,
	) => void;
	registerCurrentMerchantName: (merchantName?: string) => void;
	registerCurrentMerchantUserCount: (userCount: number) => void;
	registerPage: (page: string) => void;
	trackDateRangerPickerClick: () => void;
	trackStatusFilterSelection: (activeStatuses: string) => void;
	trackPaymentProductFilterSelection: (activeProductTypes: string) => void;
	trackPaymentProductAddProductClick: () => void;
	trackPaymentProductArchiveClick: () => void;
	trackPaymentProductEditClick: () => void;
	trackPaymentProductCopyClick: () => void;
	trackPaymentProductDeleteScheduleClick: () => void;
	trackCartsFilterSelection: (activeCartsTypes: string) => void;
	trackSearchTypeSelection: (activeSearchType: string) => void;
	trackSearchRan: (searchType: SearchTypes) => void;
	trackHelpClick: () => void;
	trackTransactionsViewPageChange: (page: number) => void;
	trackKPIClick: (status: TransactionStatuses) => void;
	trackUserManagementClick: () => void;
	trackAccountSettingsClick: () => void;
	trackHelpAndLegalClick: () => void;
	trackLegalDocumentClick: (documentName: string) => void;
	trackNotificationAlertsClick: () => void;
	trackUserNameClick: () => void;
	trackLoginSuccess: () => void;
	trackLogoutClick: () => void;
	trackLogoutInactivity: () => void;
	trackAddNewUser: () => void;
	trackRemoveUser: () => void;
	trackUpdateUserRole: (role: UserRole) => void;
	trackPrimaryNavigationClick: (subNavLocation: SubNavSection | MainNavSection) => void;
	trackTransactionIDCopy: () => void;
	trackClickTransactionAction: (selectedAction?: TransactionActions) => void;
	trackClickTransactionDetailTab: (tab: TransactionDetailTab) => void;
	trackDateRangeStaticSelection: (staticRange: string) => void;
	trackDateRangeSelection: (daysSelected: number) => void;
	trackClickTooltip: (tooltipTitle?: string) => void;
	trackCSVFileDownload: () => void;
	trackAccountSettingsUpdate: (updatedSettings: string) => void;
	trackUnsavedChangesNavigation: () => void;
	trackDiscardAccountSettingsChanges: () => void;
	trackDeleteEmailNotification: (programID: string) => void;
	trackAddEmailNotification: (programID: string, notificationTypes: string) => void;
	trackUpdateEmailNotification: (programID: string, notificationTypes: string) => void;
	trackAccessKeyCopied: (accessKeyType: AccessKeyType) => void;
	trackMerchantIdCopied: () => void;
	trackNotificationLinkClick: (notificationType: NotificationCenterRowType) => void;
	trackDataInsightCardClick: (selectedDataInsightCard: MetricsOption) => void;
	trackDataInsightDateOptionClick: (selectedOption: ChartOption) => void;
	trackCreateCartClick: () => void;
	trackViewCartClick: () => void;
	trackExpireCart: () => void;
	trackResendCartEmail: () => void;
	trackCartLinkCopy: () => void;
	trackCreateCartEnterCartTotal: () => void;
	trackCreateCartAddItemsToCart: () => void;
	trackCreateCartAddOrderItem: () => void;
	trackCreateCartEditOrderItem: () => void;
	trackCreateCartRemoveOrderItem: () => void;
	trackCreateCartSaveCart: () => void;
	trackEditCartDiscardChanges: () => void;
	trackEditCartSaveChanges: () => void;
	trackViewCartGoBackToCartsList: () => void;
	trackCreateCartDiscardCart: () => void;
	trackCreateCartAccordionAction: (section: Sections) => void;
	trackTransactionListExportClick: () => void;
	trackMerchantPlacementThemeSelection: (theme: ButtonTheme) => void;
	trackGeneratingLocationVerificationCodes: () => void;
	trackLocationVerificationCodeCopy: () => void;
	trackCheckoutDesign: () => void;
	trackCheckoutDesignSave: () => void;
	trackCheckoutDesignCancel: () => void;
	trackPrimaryColorGradient: () => void;
	trackHashCodePrimaryColor: () => void;
	trackImageInsert: () => void;
}
